import type { Icon } from '@tabler/icons-react';
import {
  IconAdjustmentsHorizontal,
  IconBell,
  IconCreditCard,
  IconEye,
  IconLayoutDashboard,
  IconPalette,
  IconShieldLock,
  IconUser,
} from '@tabler/icons-react';
import { CREATOR_SCORE_ANCHOR } from '~/components/Account/creator-score-copy';

export const accountSectionGroups = [
  { id: 'account', label: 'Account' },
  { id: 'content', label: 'Content' },
  { id: 'access', label: 'Billing & access' },
] as const;

export type AccountSectionGroupId = (typeof accountSectionGroups)[number]['id'];

export type AccountSection = {
  id: string;
  /** URL segment under `/user/account`. Empty string is the index. */
  path: string;
  label: string;
  icon: Icon;
  group: AccountSectionGroupId;
  /**
   * What the search box matches on. Labels alone only ever find the section you already
   * know the name of, which is the case that needed no search.
   */
  keywords: string[];
};

export const accountSections: AccountSection[] = [
  {
    id: 'overview',
    path: '',
    label: 'Overview',
    icon: IconLayoutDashboard,
    group: 'account',
    keywords: ['membership', 'buzz', 'standing', 'email', 'summary'],
  },
  {
    id: 'profile',
    path: 'profile',
    label: 'Profile & Account',
    icon: IconUser,
    group: 'account',
    keywords: [
      'username',
      'email',
      'social links',
      'sponsorship',
      'creator score',
      'strikes',
      'standing',
      'delete account',
      'refresh session',
    ],
  },
  {
    id: 'preferences',
    path: 'preferences',
    label: 'Preferences',
    icon: IconAdjustmentsHorizontal,
    group: 'account',
    keywords: [
      'autoplay',
      'gifs',
      'image format',
      'model format',
      'precision',
      'quant',
      'assistant',
      'civbot',
      'chats',
      'blue buzz',
      'early adopter',
      'privacy',
      'reactions',
      'who reacted',
      'who collected',
      'older reactions',
      'video controls',
      'advanced mode',
      'air',
    ],
  },
  {
    id: 'notifications',
    path: 'notifications',
    label: 'Notifications',
    icon: IconBell,
    group: 'account',
    keywords: ['email notifications', 'on-site', 'comments', 'milestones', 'buzz', 'moderation'],
  },
  {
    id: 'content',
    path: 'content',
    label: 'Content & Browsing',
    icon: IconEye,
    group: 'content',
    keywords: ['mature', 'nsfw', 'browsing level', 'blur', 'hidden tags', 'hidden users'],
  },
  {
    id: 'creator',
    path: 'creator',
    label: 'Creator',
    icon: IconPalette,
    group: 'content',
    keywords: [
      'stickers',
      'placement',
      'remix gallery',
      'donation goals',
      'download count',
      'generation count',
      'earned buzz',
      'creator showcase',
      'hat turf',
      'hats',
    ],
  },
  {
    id: 'billing',
    path: 'billing',
    label: 'Membership & Billing',
    icon: IconCreditCard,
    group: 'access',
    keywords: [
      'subscription',
      'membership',
      'payment methods',
      'cards',
      'payouts',
      'stripe',
      'tipalti',
      'gift',
    ],
  },
  {
    id: 'security',
    path: 'security',
    label: 'Security & Apps',
    icon: IconShieldLock,
    group: 'access',
    keywords: [
      'sign in',
      'connected accounts',
      'api keys',
      'oauth',
      'connected apps',
      'app storage',
      'app data',
    ],
  },
];

export function searchAccountSections(query: string) {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return accountSections;
  return accountSections.filter(
    (section) =>
      section.label.toLowerCase().includes(trimmed) ||
      section.keywords.some((keyword) => keyword.includes(trimmed))
  );
}

export const defaultAccountSection = accountSections[0];

/** On mobile the index renders the section MENU, so the overview needs a URL of its own. */
export const overviewSectionPath = 'overview';

export function getAccountSectionHref(section: AccountSection) {
  return section.path ? `/user/account/${section.path}` : '/user/account';
}

export function getOverviewHref() {
  return `/user/account/${overviewSectionPath}`;
}

export function resolveAccountSection(path: string | undefined) {
  if (!path) return defaultAccountSection;
  if (path === overviewSectionPath) return defaultAccountSection;
  return accountSections.find((section) => section.path === path);
}

/**
 * Anchors that already point into this page from places we cannot edit — Stripe and Tipalti
 * `return_url`s stored provider-side, and strike emails already delivered. A fragment never
 * reaches the server, so these cannot be handled by a `next.config` redirect; the shell maps
 * them on mount instead. Removing an entry silently strands whatever still emits it.
 */
export const legacyAnchorSections: Record<string, string> = {
  accounts: 'security',
  'api-keys': 'security',
  [CREATOR_SCORE_ANCHOR]: 'profile',
  'creator-controls': 'creator',
  'manage-subscription': 'billing',
  'notification-settings': 'notifications',
  'payment-methods': 'billing',
  payments: 'billing',
  strikes: 'profile',
};

export function resolveLegacyAnchor(hash: string) {
  const key = hash.replace(/^#/, '').toLowerCase();
  const sectionId = legacyAnchorSections[key];
  if (!sectionId) return undefined;
  return accountSections.find((section) => section.id === sectionId);
}

// Keeps the fragment so the pane can scroll to it, and returns nothing once already on the target
// pane: the redirected URL still carries the same legacy hash, so a second pass must be a no-op.
export function resolveLegacyAnchorRedirect({
  pathname,
  search,
  hash,
}: {
  pathname: string;
  search: string;
  hash: string;
}) {
  const section = resolveLegacyAnchor(hash);
  if (!section) return undefined;
  const target = getAccountSectionHref(section);
  if (pathname === target) return undefined;
  return {
    pathname: target,
    query: Object.fromEntries(new URLSearchParams(search).entries()),
    hash: hash.replace(/^#/, ''),
  };
}
